import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { mockListActivos, mockGetActivo } from '../data/activosMock';

/**
 * useActivos({ slug, scope, page, pageSize, filters, search })
 *
 * Listado paginado de activos de un tipo concreto.
 *
 * ───────────────────────────────────────────────────────────────────────
 * MODO MOCK (mientras II.4.5 no esté aplicado)
 * ───────────────────────────────────────────────────────────────────────
 * USE_MOCK = true → datos de data/activosMock.js.
 *
 * EL LUNES, tras aplicar II.4.5: const USE_MOCK = false;
 * y tira de los endpoints reales del backend II.4.5:
 *
 *   GET /api/inventario/activos/:scope/:slug?page&pageSize&search&<filtros>
 *     scope = 'int' (interno) | 'colab'
 *     (también existe el alias sin scope que asume int)
 *
 *   Respuesta: { data: [...], pagination: { page, pageSize, total } }
 * ───────────────────────────────────────────────────────────────────────
 */

const USE_MOCK = false;
const MOCK_DELAY = 250;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// El backend usa ?scope=interno|colab (query param), NO un segmento de
// ruta. Normalizamos el scope lógico al valor que espera el backend
// (cualquier cosa distinta de 'colab' → 'interno', retrocompatible).
function normScope(scope) {
  return scope === 'colab' ? 'colab' : 'interno';
}

export function useActivos({ slug, scope, page, pageSize, filters, search }) {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      let result;
      if (USE_MOCK) {
        await wait(MOCK_DELAY);
        result = mockListActivos({ slug, scope, page, pageSize, filters, search });
      } else {
        const params = {
          page,
          pageSize,
          scope: normScope(scope),
          ...(search ? { search } : {}),
          ...filters,
        };
        result = await api.get(`/activos/${slug}`, params);
      }
      // Evitar pisar resultados si llegó una petición más nueva
      if (myReq !== reqId.current) return;
      setData(result.data || []);
      setPagination(result.pagination || { page, pageSize, total: 0 });
    } catch (err) {
      if (myReq === reqId.current) setError(err);
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [slug, scope, page, pageSize, search, JSON.stringify(filters)]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, pagination, loading, error, refresh: load, isMock: USE_MOCK };
}

/** Detalle de un activo (para el drawer). */
export async function fetchActivo({ slug, scope, id }) {
  if (USE_MOCK) {
    await wait(120);
    return mockGetActivo({ slug, scope, id });
  }
  const res = await api.get(`/activos/${slug}/${id}`, { scope: normScope(scope) });
  return res?.data || res;
}

/**
 * Alta de un activo. El backend (B2) genera la `referencia`
 * automáticamente; aquí solo se envían los campos del fields_schema.
 * `api.post(path, data)` no acepta query params, así que el scope
 * viaja en el query string del propio path.
 * Devuelve el activo creado (el endpoint responde objeto directo).
 */
export async function createActivo({ slug, scope, payload }) {
  const res = await api.post(
    `/activos/${slug}?scope=${normScope(scope)}`,
    payload
  );
  return res?.data || res;
}
